import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Search, ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

const EMPTY = { name: '', description: '', media_url: '', type: 'reps', muscle_group: '', default_rest_seconds: 60 };

export default function ExerciseManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [wgerOpen, setWgerOpen] = useState(false);
    const [wgerTerm, setWgerTerm] = useState('');
    const [wgerResults, setWgerResults] = useState([]);
    const [wgerLoading, setWgerLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/exercises');
            setItems(r.data.sort((a, b) => a.name.localeCompare(b.name)));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
    const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };

    const save = async () => {
        if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
        try {
            if (editing) {
                await api.put(`/exercises/${editing.id}`, form);
                toast.success('Ejercicio actualizado');
            } else {
                await api.post('/exercises', form);
                toast.success('Ejercicio creado');
            }
            setOpen(false);
            await load();
        } catch (e) {
            toast.error('Error al guardar');
        }
    };

    const del = async (it) => {
        if (!window.confirm(`¿Borrar "${it.name}"? Se eliminará también de todas tus rutinas.`)) return;
        await api.delete(`/exercises/${it.id}`);
        toast.success('Ejercicio borrado');
        await load();
    };

    const searchWger = async () => {
        setWgerLoading(true);
        try {
            const r = await api.get(`/exercises/wger/search?term=${encodeURIComponent(wgerTerm)}&lang=4`);
            setWgerResults(r.data);
            if (r.data.length === 0) {
                // fallback to English
                const r2 = await api.get(`/exercises/wger/search?term=${encodeURIComponent(wgerTerm)}&lang=2`);
                setWgerResults(r2.data);
            }
        } finally { setWgerLoading(false); }
    };
    const importFromWger = async (w) => {
        await api.post('/exercises', {
            name: w.name,
            description: (w.description || '').replace(/<[^>]+>/g, ''),
            media_url: w.media_url || '',
            type: 'reps',
            muscle_group: w.muscle_group || '',
            default_rest_seconds: 60,
        });
        toast.success(`Importado: ${w.name}`);
        await load();
    };

    const filtered = items.filter((it) => it.name.toLowerCase().includes(q.toLowerCase()) || (it.muscle_group || '').toLowerCase().includes(q.toLowerCase()));

    return (
        <div className="px-4 lg:px-8 py-4 lg:py-8 max-w-[1400px]">
            <header className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="text-xs text-muted-foreground uppercase tracking-widest">Biblioteca</div>
                    <h1 className="text-3xl md:text-4xl font-display font-semibold">Ejercicios</h1>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button variant="secondary" onClick={() => setWgerOpen(true)} data-testid="open-wger-import"><Search className="h-4 w-4 mr-2" /> Importar de wger</Button>
                    <Button onClick={openNew} data-testid="new-exercise-button"><Plus className="h-4 w-4 mr-2" /> Nuevo</Button>
                </div>
            </header>

            <div className="mb-4">
                <Input placeholder="Buscar ejercicio…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="exercise-search-input" />
            </div>

            {loading ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-[var(--radius)]" />)}
                </div>
            ) : filtered.length === 0 ? (
                <Card className="bg-card border-border p-10 text-center text-muted-foreground" data-testid="empty-exercises">
                    No hay ejercicios. <button className="underline" onClick={openNew}>Crea tu primero</button>.
                </Card>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.map((it) => (
                        <Card key={it.id} className="bg-card border-border p-4" data-testid={`exercise-card-${it.id}`}>
                            <div className="flex gap-3">
                                <div className="h-16 w-16 rounded-lg overflow-hidden bg-[hsl(var(--surface-2))] border border-border flex-shrink-0 grid place-items-center">
                                    {it.media_url ? (
                                        <img src={it.media_url} alt={it.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                    ) : <ImageIcon className="h-5 w-5 text-muted-foreground" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium truncate">{it.name}</div>
                                    <div className="flex gap-1 mt-1 flex-wrap">
                                        {it.muscle_group && <Badge variant="secondary" className="text-[10px]">{it.muscle_group}</Badge>}
                                        <Badge variant="outline" className="text-[10px]">{it.type === 'time' ? 'Tiempo' : 'Repeticiones'}</Badge>
                                    </div>
                                    {it.description && <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{it.description}</div>}
                                </div>
                            </div>
                            <div className="flex justify-end gap-1 mt-3">
                                <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-exercise-${it.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => del(it)} data-testid={`delete-exercise-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Edit/New Dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>{editing ? 'Editar ejercicio' : 'Nuevo ejercicio'}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Nombre</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="exercise-name-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Tipo</Label>
                                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                    <SelectTrigger data-testid="exercise-type-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="reps">Repeticiones</SelectItem>
                                        <SelectItem value="time">Tiempo</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Grupo muscular</Label>
                                <Input value={form.muscle_group} onChange={(e) => setForm({ ...form, muscle_group: e.target.value })} placeholder="Ej. Pecho, Core…" data-testid="exercise-muscle-input" />
                            </div>
                        </div>
                        <div>
                            <Label>URL de imagen / GIF</Label>
                            <Input value={form.media_url} onChange={(e) => setForm({ ...form, media_url: e.target.value })} placeholder="https://…" data-testid="exercise-media-input" />
                            {form.media_url && (
                                <div className="mt-2 aspect-square max-w-[260px] mx-auto w-full rounded-lg overflow-hidden border border-border bg-white grid place-items-center">
                                    <img src={form.media_url} alt="preview" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                </div>
                            )}
                        </div>
                        <div>
                            <Label>Descripción (opcional)</Label>
                            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="exercise-description-input" />
                        </div>
                        <div>
                            <Label>Descanso por defecto (segundos)</Label>
                            <Input type="number" value={form.default_rest_seconds} onChange={(e) => setForm({ ...form, default_rest_seconds: parseInt(e.target.value || '60', 10) })} data-testid="exercise-rest-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={save} data-testid="save-exercise-button">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* wger Import Dialog */}
            <Dialog open={wgerOpen} onOpenChange={setWgerOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Importar desde wger</DialogTitle></DialogHeader>
                    <div className="flex gap-2">
                        <Input placeholder="Buscar ejercicio… (ej. push, sentadilla, plancha)" value={wgerTerm} onChange={(e) => setWgerTerm(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchWger()} data-testid="wger-search-input" />
                        <Button onClick={searchWger} disabled={wgerLoading} data-testid="wger-search-button">
                            {wgerLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                        </Button>
                    </div>
                    <div className="max-h-[50vh] overflow-auto scroll-soft space-y-2">
                        {wgerResults.map((w, i) => (
                            <div key={i} className="flex gap-3 p-3 rounded-lg border border-border bg-[hsl(var(--surface-2))]">
                                <div className="h-16 w-16 rounded-lg overflow-hidden bg-[hsl(var(--surface-3))] flex-shrink-0 grid place-items-center">
                                    {w.media_url ? <img src={w.media_url} alt={w.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <ImageIcon className="h-5 w-5 text-muted-foreground" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium">{w.name}</div>
                                    <div className="text-xs text-muted-foreground">{w.muscle_group}</div>
                                </div>
                                <Button size="sm" onClick={() => importFromWger(w)} data-testid={`wger-import-${i}`}>Importar</Button>
                            </div>
                        ))}
                        {wgerResults.length === 0 && !wgerLoading && <div className="text-sm text-muted-foreground text-center py-6">Busca en la biblioteca pública de wger</div>}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
